/**
 * Device-direct uploads (W5.1, backend services/storage/directUpload.js).
 *
 * When the institution's files are in S3, this device sends its recording
 * segments, and the phone its full frames a minute at a time, straight to
 * S3 with a URL the server signed — the exam server never carries those
 * bytes. Every step falls back to the upload route it replaces: the server
 * says 409 DIRECT_OFF (files are on the server's own disk), or the PUT to S3
 * fails (the hall's network blocks it, a proxy, a clock far out), or the
 * confirm does not answer. The fallback sends the same bytes the old way, so
 * nothing recorded is lost to this.
 *
 * The PUT goes with fetch, not the API client: S3 must not receive our
 * sign-in token, and the headers sent must be exactly the ones signed.
 */

// A hall whose network blocks S3 fails every PUT: after this many in a row,
// this page load stops trying and uses the upload route.
const PUT_FAILURES_BEFORE_GIVING_UP = 2;

export const BUNDLE_TYPE = 'application/vnd.intelversity.bundle';

// The browser's fetch, called as a method of window (an unbound fetch throws "Illegal invocation").
const browserFetch = typeof window !== 'undefined' && typeof window.fetch === 'function'
  ? (...args) => window.fetch(...args)
  : undefined;

/** PUT `body` to the presigned upload the server minted; true when S3 took it. */
export async function putToStorage(upload, body, fetchImpl = browserFetch) {
  if (!upload || !upload.url || typeof fetchImpl !== 'function') return false;
  try {
    const res = await fetchImpl(upload.url, { method: upload.method || 'PUT', headers: upload.headers || {}, body });
    return Boolean(res && res.ok);
  } catch {
    return false;
  }
}

/**
 * Recording segments: try S3 first, fall back to the upload route.
 * `state` is the page's memory ({ off, failures }) — one per page load.
 * Returns the server's answer (as the upload route's), or null to say
 * "use the upload route". A refusal the upload route would give too (the
 * attempt closed, recording off, too large, throttled) is thrown, so the
 * recorder treats it exactly as before.
 */
export async function uploadSegmentDirect(api, examId, { blob, seq, startedAt, durationMs, type }, state, fetchImpl) {
  if (state.off) return null;
  let mint;
  try {
    mint = (await api.post(`/exam/exams/${examId}/recording-chunks/direct`, {
      seq, startedAt, durationMs, type, bytes: blob.size, clientSentAt: Date.now(),
    })).data;
  } catch (e) {
    const status = e?.response?.status;
    if (e?.response?.data?.code === 'DIRECT_OFF' || status === 404) {
      state.off = true;
      return null;
    }
    if (status && status < 500) throw e;
    return null; // the network or the server failed: the upload route may still get through
  }
  if (mint.duplicate) return mint;
  const ok = await putToStorage(mint.upload, new Blob([blob], { type }), fetchImpl);
  if (!ok) {
    state.failures = (state.failures || 0) + 1;
    if (state.failures >= PUT_FAILURES_BEFORE_GIVING_UP) state.off = true;
    return null;
  }
  state.failures = 0;
  try {
    return (await api.post(`/exam/exams/${examId}/recording-chunks/direct/confirm`, { ticket: mint.ticket })).data;
  } catch {
    // Not confirmed: the upload route stores it (idempotent on seq — a
    // confirm that did land makes that a duplicate, not a second copy).
    return null;
  }
}

/**
 * The bundle container (backend services/storage/bundles.js): "IVB1", the
 * header's length (4 bytes, big-endian), the header JSON
 * { v: 1, members: [{ name, type, at, size }] }, then the members' bytes.
 */
export function packBundle(members) {
  const header = JSON.stringify({
    v: 1,
    members: members.map((m) => ({ name: m.name, type: m.type || 'image/jpeg', at: m.at ?? null, size: m.blob.size })),
  });
  const headerBlob = new Blob([header]);
  const pre = new Uint8Array(8);
  pre.set([0x49, 0x56, 0x42, 0x31]);
  new DataView(pre.buffer).setUint32(4, headerBlob.size);
  return new Blob([pre, headerBlob, ...members.map((m) => m.blob)], { type: BUNDLE_TYPE });
}

/**
 * Send one minute of the phone's frames: straight to S3, else through the
 * API. `post(suffix, body, config)` posts to .../second-camera/<token>/<suffix>
 * (the caller renews a refused token). Returns "direct", "api", "stored"
 * (the minute was already there) or "lost".
 */
export async function sendMinute({ post, window, members, fetchImpl, state = {} }) {
  if (!members.length) return 'stored';
  const bundle = packBundle(members);
  if (!state.off) {
    try {
      const mint = (await post('bundle/direct', { window, bytes: bundle.size })).data;
      if (await putToStorage(mint.upload, bundle, fetchImpl)) {
        state.failures = 0;
        await post('bundle/direct/confirm', { window, ticket: mint.ticket });
        return 'direct';
      }
      state.failures = (state.failures || 0) + 1;
      if (state.failures >= PUT_FAILURES_BEFORE_GIVING_UP) state.off = true;
    } catch (e) {
      if (e?.response?.data?.code === 'ALREADY_STORED') return 'stored';
      if (e?.response?.data?.code === 'DIRECT_OFF') state.off = true;
    }
  }
  try {
    const form = new FormData();
    form.append('window', String(window));
    form.append('bundle', bundle, 'minute.ivb');
    await post('bundle', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return 'api';
  } catch (e) {
    return e?.response?.data?.code === 'ALREADY_STORED' ? 'stored' : 'lost';
  }
}

/**
 * The phone's minute in the making: each frame under the member name the
 * server's frame reply gave it ({ window, member }); when a reply names a new
 * minute, the one before is sent (`send(minute)`); flush() sends what is left
 * (the exam ended, the page is going away).
 */
export function createMinuteBundler(send) {
  let current = null;
  return {
    add(direct, blob) {
      if (!direct || !direct.member) return;
      if (current && current.window !== direct.window) {
        const done = current;
        current = null;
        send(done);
      }
      if (!current) current = { window: direct.window, members: [] };
      current.members.push({ name: direct.member, type: 'image/jpeg', at: Number(String(direct.member).split('-')[0]) || null, blob });
    },
    flush() {
      if (!current) return;
      const done = current;
      current = null;
      send(done);
    },
    pending: () => (current ? current.members.length : 0),
  };
}
